/**
 * Callout — typed variant wrapper around the DS Alert.
 *
 * The DS Alert ships with "default" and "destructive" variants. We extend
 * it via className (the standard shadcn pattern) to cover the full range
 * of content admonitions: info, success, warning, danger, tip, quote,
 * question, note.
 *
 * Under the hood it's:
 *   <Alert className="variant-specific-colors">
 *     <Icon />
 *     <AlertTitle>{title}</AlertTitle>
 *     <AlertDescription>{children}</AlertDescription>
 *   </Alert>
 *
 * This gives us the DS's accessible grid layout, data-slot attributes,
 * and future base-style upgrades — while adding the variant colors this
 * app needs.
 */

import type { ComponentType, ReactNode, SVGProps } from "react";
import { Alert, AlertDescription, AlertTitle } from "@taiga-platform/ui";
import {
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Info,
  Lightbulb,
  Quote,
  TriangleAlert,
} from "lucide-react";

export type CalloutVariant =
  | "note"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "tip"
  | "quote"
  | "question";

interface VariantStyle {
  className: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const VARIANTS: Record<CalloutVariant, VariantStyle> = {
  note: {
    className: "border-border bg-muted/40 text-foreground [&>svg]:text-muted-foreground",
    icon: Info,
  },
  info: {
    className: "border-info/30 bg-info/10 text-info [&>svg]:text-info",
    icon: Info,
  },
  success: {
    className: "border-success/30 bg-success/10 text-success [&>svg]:text-success",
    icon: CheckCircle2,
  },
  warning: {
    className: "border-warning/30 bg-warning/15 text-warning [&>svg]:text-warning",
    icon: TriangleAlert,
  },
  danger: {
    className: "border-destructive/30 bg-destructive/10 text-destructive [&>svg]:text-destructive",
    icon: AlertCircle,
  },
  tip: {
    className: "border-primary/30 bg-primary-subtle text-primary [&>svg]:text-primary",
    icon: Lightbulb,
  },
  quote: {
    className: "border-primary/30 bg-primary-subtle text-primary [&>svg]:text-primary",
    icon: Quote,
  },
  question: {
    className: "border-primary/30 bg-primary-subtle text-primary [&>svg]:text-primary",
    icon: HelpCircle,
  },
};

interface CalloutProps {
  variant?: CalloutVariant;
  title?: string;
  children: ReactNode;
}

export function Callout({ variant = "info", title, children }: CalloutProps) {
  const style = VARIANTS[variant];
  const Icon = style.icon;

  return (
    <Alert className={`not-prose my-6 ${style.className}`}>
      <Icon />
      {title && <AlertTitle className="text-current">{title}</AlertTitle>}
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}
